import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  useTheme,
  Chip,
} from "@mui/material";
import {
  Inventory as InventoryIcon,
  CalendarMonth as CalendarIcon,
  Science as ScienceIcon,
  People as PeopleIcon,
  Check as CheckIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { Link as RouterLink, Navigate } from "react-router-dom";
import Layout from "../components/common/Layout";
import { useAuth } from "../context/AuthContext";
import { inventoryAPI, labAPI, bookingAPI } from "../utils/api";
import LowStockWidget from "../components/admin/LowStockWidget";

const StatCard = ({ title, value, icon, color }) => {
  const theme = useTheme();

  return (
    <Card
      raised={false}
      sx={{
        height: "100%",
        bgcolor: color,
        color: "white",
        borderRadius: 2,
        transition: "transform 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: theme.shadows[8],
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Avatar
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.2)",
                width: { xs: 48, sm: 56 },
                height: { xs: 48, sm: 56 },
              }}
            >
              {icon}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography
              variant="h3"
              component="div"
              sx={{
                fontSize: { xs: "1.8rem", sm: "2.2rem" },
                fontWeight: "bold",
              }}
            >
              {value}
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.8 }}>
              {title}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Helper to parse Firestore timestamp or JS Date into JS Date object
const parseTimestamp = (raw) => {
  if (raw instanceof Date) return raw;
  if (raw?._seconds) return new Date(raw._seconds * 1000);
  if (raw?.seconds) return new Date(raw.seconds * 1000);
  if (raw?.toDate instanceof Function) return raw.toDate();
  if (typeof raw === "string" || typeof raw === "number") return new Date(raw);
  return new Date();
};

const Dashboard = () => {
  const { currentUser, userProfile } = useAuth();
  const theme = useTheme();

  const [stats, setStats] = useState({
    inventoryCount: 0,
    labsCount: 0,
    pendingBookings: 0,
    userCount: 0,
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch data for dashboard
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // Fetch all data in parallel
        const [inventoryData, labsData, bookingsData] = await Promise.all([
          inventoryAPI.getAll(),
          labAPI.getAll(),
          bookingAPI.getAll(),
        ]);

        setStats({
          inventoryCount: inventoryData.data.length || 0,
          labsCount: labsData.data.length || 0,
          pendingBookings:
            bookingsData.data.filter((b) => b.status === "pending").length || 0,
          userCount: 24, // This would typically come from a user API endpoint
        });

        // Get recent bookings
        setRecentBookings(bookingsData.data.slice(0, 5));
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        // Set some mock data for development purposes
        setStats({
          inventoryCount: 45,
          labsCount: 8,
          pendingBookings: 12,
          userCount: 24,
        });
        setRecentBookings([
          {
            _id: "1",
            resourceType: "lab_session",
            resourceId: { name: "Computer Science Lab A" },
            startTime: new Date(),
            status: "approved",
          },
          {
            _id: "2",
            resourceType: "equipment",
            resourceId: { name: "Oscilloscope" },
            startTime: new Date(),
            status: "pending",
          },
          {
            _id: "3",
            resourceType: "lab_session",
            resourceId: { name: "Physics Laboratory" },
            startTime: new Date(Date.now() + 86400000),
            status: "rejected",
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Different role-based UIs could be implemented here
  const renderRoleBasedContent = () => {
    switch (userProfile?.role) {
      case "developer-master":
        // Dynamically import the DeveloperDashboard to avoid loading it unnecessarily
        const DeveloperDashboard = React.lazy(() =>
          import("../components/developer/DeveloperDashboard")
        );
        return (
          <React.Suspense fallback={<div>Loading Developer Dashboard...</div>}>
            <DeveloperDashboard />
          </React.Suspense>
        );
      case "admin":
        return renderAdminDashboard();
      case "faculty":
        return <Navigate to="/faculty" replace />;
      case "scholar":
        return <Navigate to="/scholar" replace />;
      case "worker":
        return <Navigate to="/worker" replace />;
      case "hod":
        return <Navigate to="/hod" replace />;
      case "manager":
        return <Navigate to="/manager" replace />;
      case "user":
        return <Navigate to="/user" replace />;
      case "student":
        return renderStudentDashboard();
      default:
        return renderDefaultDashboard();
    }
  };

  const renderAdminDashboard = () => (
    <Grid container spacing={3}>
      {/* First row: Main stat cards and System Logbook */}
      <Grid item xs={12} sm={6} md={2} lg={2}>
        <StatCard
          title="Inventory Items"
          value={stats.inventoryCount}
          icon={<InventoryIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />}
          color="#2196f3"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2} lg={2}>
        <StatCard
          title="Labs"
          value={stats.labsCount}
          icon={<ScienceIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />}
          color="#ff9800"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2} lg={2}>
        <StatCard
          title="Pending Bookings"
          value={stats.pendingBookings}
          icon={<CalendarIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />}
          color="#f44336"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2} lg={2}>
        <StatCard
          title="Users"
          value={stats.userCount}
          icon={<PeopleIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />}
          color="#4caf50"
        />
      </Grid>

      {/* System Logbook card moved to first row */}
      <Grid item xs={12} sm={6} md={2} lg={2}>
        <Box
          component={RouterLink}
          to="/admin/logbook"
          sx={{ textDecoration: "none" }}
        >
          <StatCard
            title="System Logbook"
            value="View Logs"
            icon={<ScheduleIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />}
            color="#9c27b0"
          />
        </Box>
      </Grid>

      {/* Second row: Low Stock and Recent Bookings side by side */}
      <Grid item xs={12} sm={6} md={6} lg={6}>
        <LowStockWidget />
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={6}>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            height: "100%",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            flexWrap="wrap"
            gap={1}
          >
            <Typography variant="h6" sx={{ fontWeight: "medium" }}>
              Recent Bookings
            </Typography>
            <Button
              component={RouterLink}
              to="/booking-system"
              variant="contained"
              size="small"
              endIcon={<ArrowForwardIcon />}
              sx={{
                borderRadius: 6,
                px: 2,
                boxShadow: "none",
                "&:hover": {
                  boxShadow: theme.shadows[2],
                },
              }}
            >
              View All
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List disablePadding>
              {recentBookings.map((booking) => (
                <ListItem
                  key={booking._id}
                  divider
                  sx={{
                    px: { xs: 1, sm: 2 },
                    py: 1.5,
                    borderRadius: 1,
                    mb: 1,
                    "&:last-child": {
                      borderBottom: "none",
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor:
                          booking.status === "approved"
                            ? "success.main"
                            : booking.status === "pending"
                            ? "warning.main"
                            : "error.main",
                      }}
                    >
                      {booking.status === "approved" ? (
                        <CheckIcon />
                      ) : booking.status === "pending" ? (
                        <ScheduleIcon />
                      ) : (
                        <WarningIcon />
                      )}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                        {booking.resourceId?.name ||
                          (booking.resourceType === "lab_session"
                            ? "Lab Session"
                            : "Equipment")}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {parseTimestamp(booking.startTime).toLocaleString(
                          undefined,
                          {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )}
                      </Typography>
                    }
                    sx={{
                      mr: 1,
                      ".MuiListItemText-secondary": {
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                  <Chip
                    size="small"
                    label={booking.status}
                    sx={{
                      textTransform: "capitalize",
                      fontWeight: "medium",
                      color: "white",
                      bgcolor:
                        booking.status === "approved"
                          ? "success.main"
                          : booking.status === "pending"
                          ? "warning.main"
                          : "error.main",
                    }}
                  />
                </ListItem>
              ))}
              {recentBookings.length === 0 && (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <Typography color="text.secondary">
                    No recent bookings
                  </Typography>

                  <Button
                    component={RouterLink}
                    to="/booking-system?new=true"
                    variant="outlined"
                    size="small"
                    sx={{ mt: 2, borderRadius: 6 }}
                  >
                    Create Booking
                  </Button>
                </Box>
              )}
            </List>
          )}

          {recentBookings.length > 0 && !loading && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button
                component={RouterLink}
                to="/booking-system?new=true"
                variant="outlined"
                size="small"
                sx={{ borderRadius: 6 }}
              >
                Create New Booking
              </Button>
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );

  // Simplified versions for other roles
  const renderStudentDashboard = () => renderAdminDashboard();
  const renderDefaultDashboard = () => renderAdminDashboard();

  return (
    <Layout title="Dashboard">
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontSize: { xs: "1.75rem", sm: "2.125rem" },
            fontWeight: "bold",
          }}
        >
          Welcome, {userProfile?.name || currentUser?.displayName || "User"}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Here's what's happening today
        </Typography>
      </Box>

      {renderRoleBasedContent()}
    </Layout>
  );
};

export default Dashboard;
